import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  markAllNotificationsRead,
  markNotificationRead,
  selectNotificationPreviewError,
  selectNotificationPreviewItems,
  selectNotificationPreviewLoading,
  selectUnreadNotificationCount,
} from 'store/slices/notificationsSlice';
import { selectSettings } from 'store/slices/authSlice';
import NotificationDropdownItem from './NotificationDropdownItem';

function NotificationDropdownSkeleton() {
  return (
    <div className="divide-y divide-white/10">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="px-4 py-4">
          <div className="h-4 w-2/3 animate-pulse rounded bg-white/10" />
          <div className="mt-2 h-3 w-full animate-pulse rounded bg-white/10" />
          <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-white/10" />
        </div>
      ))}
    </div>
  );
}

export default function NotificationDropdownPanel({ onClose }) {
  const dispatch = useDispatch();
  const previewItems = useSelector(selectNotificationPreviewItems);
  const loading = useSelector(selectNotificationPreviewLoading);
  const error = useSelector(selectNotificationPreviewError);
  const unreadCount = useSelector(selectUnreadNotificationCount);
  const settings = useSelector(selectSettings);

  const handleSelect = useCallback(async (notification) => {
    try {
      if (!notification.isRead) {
        await dispatch(markNotificationRead(notification._id)).unwrap();
      }
    } catch (_error) {
      // Redux keeps the error state.
    }
  }, [dispatch]);

  const handleMarkAllRead = async () => {
    try {
      await dispatch(markAllNotificationsRead()).unwrap();
    } catch (_error) {
      // Error state is already stored in redux.
    }
  };

  const handleViewAll = () => {
    onClose?.();
  };

  return (
    <div className="overflow-hidden rounded-md">
      <div className="flex items-start justify-between gap-4 border-b border-white/10 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-white">Notifications</h2>
          <p className="mt-1 text-xs text-gray-400">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAllRead}
            className="text-xs font-semibold text-indigo-400 transition hover:text-indigo-300 cursor-pointer"
          >
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <NotificationDropdownSkeleton />
      ) : error ? (
        <div className="px-4 py-6 text-sm text-red-300">
          Failed to load notifications.
        </div>
      ) : previewItems.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400">
          No notifications yet.
        </div>
      ) : (
        <ul role="list" className="divide-y divide-white/10">
          {previewItems.map((item) => (
            <NotificationDropdownItem
              key={item._id}
              item={item}
              settings={settings}
              onSelect={handleSelect}
            />
          ))}
        </ul>
      )}

      <div className="border-t border-white/10 px-4 py-3">
        <Link
          to="/notifications"
          onClick={handleViewAll}
          className="text-sm font-semibold text-indigo-400 transition hover:text-indigo-300"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}
